import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { uniqBy } from 'lodash';

import {
  FileTreeItemType,
  RyeconColorDbt,
  RyeconDatabase,
  RyeconDatabaseSchema,
  useOpenDirectories,
} from '@dbt-labs/sourdough';

import { useLinkToExplorer } from '@/dbt-explorer';
import { Project, useProjectsLazy } from '@/hooks/admin/project';
import { getDefaultEnvironmentIds } from '@/hooks/admin/types';
import { useAllAppliedViewByLazy } from '@/hooks/discovery/allAppliedViewBy';
import { discoveryLineageResourceTypes } from '@/hooks/discovery/appliedLineage';
import { UrlEnvironmentType } from '@/typings/url';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import {
  DBT_DATABASE_ROOT_NODE_NAME,
  DBT_ROOT_NODE_LABEL,
  PROJECT_PAGE_SIZE,
} from './const';
import { DbtInternalTree, DbtTreeData } from './types';
import { generateFileWithLogo, generateFolderWithIcon } from './utils';

type DatabaseName = string;
type SchemaName = string;
type DatabaseSet = Record<DatabaseName, Set<SchemaName>>;
type DatabaseMap = Record<DatabaseName, SchemaName[]>;

// Per-project database map, keyed by projectId
type ProjectDatabaseMaps = Record<number, DatabaseMap>;

const sortDatabases = (databaseSet: DatabaseSet): DatabaseMap => {
  return Object.keys(databaseSet)
    .sort((a, b) => a.localeCompare(b))
    .reduce<DatabaseMap>((acc, database) => {
      acc[database] = Array.from(databaseSet[database]).sort((a, b) =>
        a.localeCompare(b),
      );
      return acc;
    }, {});
};

export const useDbtDatabaseTree = ({
  allProjects,
  setAllProjects,
  projectsPageInfo,
  setProjectsPageInfo,
  viewParam,
  selectedEnvironmentType,
}: DbtInternalTree): DbtTreeData => {
  const { accountId } = useDbtCloudAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const schemaDetailsLink = useLinkToExplorer('schemaDetails');

  const isActive = useMemo(() => viewParam === 'databaseView', [viewParam]);

  const [selectedFile, setSelectedFileState] = useState<string | undefined>();
  const [selectedFolder, setSelectedFolder] = useState<string | undefined>();

  const [dbtTree, setDbtTree] = useState<FileTreeItemType[]>([
    generateFolderWithIcon(DBT_DATABASE_ROOT_NODE_NAME, {
      iconOverride: {
        ryecon: RyeconColorDbt,
        label: DBT_ROOT_NODE_LABEL,
      },
      name: DBT_ROOT_NODE_LABEL,
      data: {
        pagination: {
          page: 0,
          hasMore: false,
          total: 0,
          isLoading: false,
        },
      },
    }),
  ]);

  const [projects, getProjects] = useProjectsLazy();
  const [, getAllAppliedViewBy] = useAllAppliedViewByLazy();

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    items: dbtTree,
    activeFilePath: selectedFile,
    rootNodeName: DBT_DATABASE_ROOT_NODE_NAME,
  });

  // Cache per-project DatabaseMaps keyed by environment type
  const databaseMapCache = useRef<{
    envType: UrlEnvironmentType;
    maps: ProjectDatabaseMaps;
  } | null>(null);

  const setTreeStateForPath = useCallback(
    (path: string, props: Partial<{ isLoading: boolean; hasMore: boolean }>) => {
      setDbtTree((prev) => {
        const newTree = [...prev];
        const updatedNode = newTree.find((node) => node.id === path);
        if (!updatedNode) return prev;
        updatedNode.data.pagination = {
          hasMore: props?.hasMore ?? updatedNode.data.pagination?.hasMore ?? false,
          page: updatedNode.data.pagination?.page ?? 0,
          total: updatedNode.data.pagination?.total ?? 0,
          isLoading:
            props?.isLoading ?? updatedNode.data.pagination?.isLoading ?? false,
        };
        return uniqBy(newTree, 'id');
      });
    },
    [],
  );

  const fetchSingleProjectDatabaseMap = useCallback(
    async (project: Project): Promise<{ projectId: number; map: DatabaseMap }> => {
      const environmentIds = getDefaultEnvironmentIds(project.environments || [], [
        selectedEnvironmentType,
      ]);
      if (environmentIds.length === 0) return { projectId: project.id!, map: {} };

      const envId = environmentIds[0];
      const result = await getAllAppliedViewBy({
        environmentId: envId,
        userId: null,
        types: discoveryLineageResourceTypes,
      });

      const lineage = result.data?.environment?.applied?.lineage ?? [];
      const databaseSet = lineage.reduce<DatabaseSet>((acc, { database, schema }) => {
        if (!database || !schema) return acc;
        const dbLower = database.toLowerCase();
        const schemaLower = schema.toLowerCase();
        acc[dbLower] ??= new Set();
        acc[dbLower].add(schemaLower);
        return acc;
      }, {});

      return { projectId: project.id!, map: sortDatabases(databaseSet) };
    },
    [selectedEnvironmentType, getAllAppliedViewBy],
  );

  const fetchProjectDatabaseMaps = useCallback(
    async (projectList: Project[]): Promise<ProjectDatabaseMaps> => {
      // Check cache — only fetch projects not already cached
      if (
        databaseMapCache.current &&
        databaseMapCache.current.envType === selectedEnvironmentType
      ) {
        const uncachedProjects = projectList.filter(
          (p) => !(p.id! in databaseMapCache.current!.maps),
        );
        if (uncachedProjects.length === 0) {
          return databaseMapCache.current.maps;
        }
        // Fetch only the missing projects, then merge
        const newResults = await Promise.allSettled(
          uncachedProjects.map(fetchSingleProjectDatabaseMap),
        );

        // Re-check cache after async — environment switch may have nulled it
        if (!databaseMapCache.current) {
          return {};
        }

        for (const result of newResults) {
          if (result.status === 'fulfilled') {
            databaseMapCache.current.maps[result.value.projectId] = result.value.map;
          }
        }

        return databaseMapCache.current.maps;
      }

      const results = await Promise.allSettled(
        projectList.map(fetchSingleProjectDatabaseMap),
      );

      const maps: ProjectDatabaseMaps = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          maps[result.value.projectId] = result.value.map;
        }
      }

      // Update cache
      databaseMapCache.current = {
        envType: selectedEnvironmentType,
        maps,
      };

      return maps;
    },
    [selectedEnvironmentType, fetchSingleProjectDatabaseMap],
  );

  const generateProjectNodes = useCallback(
    (projectList: Project[], projectMaps: ProjectDatabaseMaps): FileTreeItemType[] => {
      return projectList
        .filter((project) => {
          const map = projectMaps[project.id!];
          return map && Object.keys(map).length > 0;
        })
        .map((project) => {
          const dbCount = Object.keys(projectMaps[project.id!] || {}).length;
          return generateFolderWithIcon(
            `${DBT_DATABASE_ROOT_NODE_NAME}/${project.id}`,
            {
              name: project.name ?? '',
              data: {
                tooltipText: project.name,
                pagination: {
                  page: 0,
                  hasMore: false,
                  total: dbCount,
                  isLoading: false,
                },
              },
            },
          );
        });
    },
    [],
  );

  const generateDatabaseNodes = useCallback(
    (projectId: number, databaseMap: DatabaseMap): FileTreeItemType[] => {
      return Object.keys(databaseMap).map((dbName) =>
        generateFolderWithIcon(
          `${DBT_DATABASE_ROOT_NODE_NAME}/${projectId}/${dbName}`,
          {
            iconOverride: {
              ryecon: RyeconDatabase,
              label: dbName,
            },
            name: dbName,
            data: {
              pagination: {
                page: 0,
                hasMore: false,
                total: databaseMap[dbName].length,
                isLoading: false,
              },
            },
          },
        ),
      );
    },
    [],
  );

  const generateSchemaNodes = useCallback(
    (projectId: number, dbName: string, schemas: SchemaName[]): FileTreeItemType[] => {
      return schemas.map((schemaName) =>
        generateFileWithLogo(
          `${DBT_DATABASE_ROOT_NODE_NAME}/${projectId}/${dbName}/${schemaName}`,
          {
            iconOverride: {
              ryecon: RyeconDatabaseSchema,
              label: schemaName,
            },
            name: schemaName,
            data: {
              tooltipText: schemaName,
            },
          },
        ),
      );
    },
    [],
  );

  // Node ID structure:
  // Root:     dbt-databases
  // Project:  dbt-databases/{projectId}
  // Database: dbt-databases/{projectId}/{databaseName}
  // Schema:   dbt-databases/{projectId}/{databaseName}/{schemaName}

  const generateChildrenForPath = useCallback(
    async (path: string): Promise<void> => {
      setTreeStateForPath(path, { isLoading: true, hasMore: false });
      const parts = path.split('/');

      if (path === DBT_DATABASE_ROOT_NODE_NAME) {
        // Root level → fetch projects, then fetch lineage per project
        const projectsResult = await getProjects({
          accountId: accountId ?? 0,
          first: PROJECT_PAGE_SIZE,
          after: projectsPageInfo.after[DBT_DATABASE_ROOT_NODE_NAME] || '',
          includeRelated: ['environments'],
        });

        if (!projectsResult.data) {
          setTreeStateForPath(path, { isLoading: false, hasMore: false });
          return;
        }

        const newProjects = projectsResult.data as Project[];
        const projectList = uniqBy([...allProjects, ...newProjects], 'id');
        setAllProjects(projectList);
        setProjectsPageInfo((prev) => ({
          ...prev,
          hasNextPage: projectsResult.pageInfo?.hasNextPage ?? false,
          totalCount: projectsResult.pageInfo?.totalCount ?? 0,
          after: {
            ...prev.after,
            [DBT_DATABASE_ROOT_NODE_NAME]: projectsResult.pageInfo?.endCursor || '',
          },
        }));

        const projectMaps = await fetchProjectDatabaseMaps(projectList);
        const projectNodes = generateProjectNodes(projectList, projectMaps);

        setDbtTree((prev) => {
          const rootNode = prev.find((node) => node.id === DBT_DATABASE_ROOT_NODE_NAME);
          if (!rootNode) return prev;

          return uniqBy(
            [
              {
                ...rootNode,
                data: {
                  ...rootNode.data,
                  pagination: {
                    page: 0,
                    hasMore: projectsResult.pageInfo?.hasNextPage ?? false,
                    total: projectNodes.length,
                    isLoading: false,
                  },
                },
              },
              ...prev.filter((node) => node.id !== DBT_DATABASE_ROOT_NODE_NAME),
              ...projectNodes,
            ],
            'id',
          );
        });
      } else if (parts.length === 2) {
        // Project level: dbt-databases/{projectId} → show database folders
        const projectId = Number(parts[1]);
        const cachedMaps = databaseMapCache.current?.maps;
        const databaseMap = cachedMaps?.[projectId] ?? {};
        const databaseNodes = generateDatabaseNodes(projectId, databaseMap);

        setDbtTree((prev) => uniqBy([...prev, ...databaseNodes], 'id'));
        setTreeStateForPath(path, { isLoading: false, hasMore: false });
      } else if (parts.length === 3) {
        // Database level: dbt-databases/{projectId}/{databaseName} → show schema leaves
        const projectId = Number(parts[1]);
        const dbName = parts[2];
        const cachedMaps = databaseMapCache.current?.maps;
        const schemas = cachedMaps?.[projectId]?.[dbName] ?? [];
        const schemaNodes = generateSchemaNodes(projectId, dbName, schemas);

        setDbtTree((prev) => uniqBy([...prev, ...schemaNodes], 'id'));
        setTreeStateForPath(path, { isLoading: false, hasMore: false });
      }
    },
    [
      setTreeStateForPath,
      allProjects,
      getProjects,
      accountId,
      projectsPageInfo,
      setAllProjects,
      setProjectsPageInfo,
      fetchProjectDatabaseMaps,
      generateProjectNodes,
      generateDatabaseNodes,
      generateSchemaNodes,
    ],
  );

  // Invalidate cache when environment type changes
  useEffect(() => {
    if (!isActive) return;
    if (
      databaseMapCache.current &&
      databaseMapCache.current.envType !== selectedEnvironmentType
    ) {
      databaseMapCache.current = null;
      // Clear tree back to root and reset all selection state
      setDbtTree([
        generateFolderWithIcon(DBT_DATABASE_ROOT_NODE_NAME, {
          iconOverride: {
            ryecon: RyeconColorDbt,
            label: DBT_ROOT_NODE_LABEL,
          },
          name: DBT_ROOT_NODE_LABEL,
          data: {
            pagination: {
              page: 0,
              hasMore: false,
              total: 0,
              isLoading: false,
            },
          },
        }),
      ]);
      setSelectedFileState(undefined);
      setSelectedFolder(undefined);
      setOpenDirectories([]);
    }
  }, [selectedEnvironmentType, isActive, setOpenDirectories]);

  // Schema click → navigate to schema details page
  const handleSetSelectedFile = useCallback(
    (value: SetStateAction<string | undefined>) => {
      const newValue = typeof value === 'function' ? value(selectedFile) : value;
      if (!newValue) return;
      setSelectedFileState(newValue);

      // Parse: dbt-databases/{projectId}/{databaseName}/{schemaName}
      const parts = newValue.split('/');
      if (parts.length !== 4) return;

      const projectId = Number(parts[1]);
      const databaseName = parts[2];
      const schemaName = parts[3];

      navigate(
        schemaDetailsLink({
          accountId: accountId ?? 0,
          projectId,
          environmentType: selectedEnvironmentType,
          databaseName,
          schemaName,
        }).concat(location.search),
      );
    },
    [
      selectedFile,
      navigate,
      schemaDetailsLink,
      accountId,
      selectedEnvironmentType,
      location.search,
    ],
  );

  // Folder click (project or database) → highlight only, no navigation
  const handleSetSelectedFolder = useCallback(
    (
      value: SetStateAction<string | undefined>,
      _event?: React.MouseEvent,
      isCollapseClick = false,
    ) => {
      if (!isCollapseClick) {
        const newValue = typeof value === 'function' ? value(selectedFolder) : value;
        setSelectedFolder(newValue);
      }
    },
    [selectedFolder],
  );

  const allProjectsRef = useRef(allProjects);
  allProjectsRef.current = allProjects;

  const onSort = useCallback((a: string, b: string) => {
    const partsA = a.split('/').filter((part) => !!part);
    const partsB = b.split('/').filter((part) => !!part);
    // Project level — sort by project name (sourdough prefixes each segment with a type char)
    if (partsA.length === partsB.length && partsA.length === 2) {
      const aId = Number(partsA[1]?.substring(1));
      const bId = Number(partsB[1]?.substring(1));
      const aName = allProjectsRef.current.find((p) => p.id === aId)?.name ?? '';
      const bName = allProjectsRef.current.find((p) => p.id === bId)?.name ?? '';
      return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
    }
    // Database/schema level — sort alphabetically with numeric awareness
    const nameA = a.split('/').pop() ?? '';
    const nameB = b.split('/').pop() ?? '';
    return nameA.localeCompare(nameB, undefined, { numeric: true });
  }, []);

  return {
    fileTree: dbtTree,
    setFileTree: setDbtTree,
    isLoading: projects.fetching,
    initialized: true,
    generateChildrenForPath,
    openDirectories,
    setOpenDirectories,
    selectedFile,
    setSelectedFile: handleSetSelectedFile,
    selectedFolder,
    setSelectedFolder: handleSetSelectedFolder,
    onSort,
  };
};
